import { contentDataFiles } from "./data/manifest";
import { contentDataFileSchema } from "./content-data";
import type { ContentDataFile } from "./content-data";
import {
  registerBlock,
  registerLocaleVariant,
  registerRelationship,
} from "./registry";
import type { ContentBlock } from "./types";
import type { ContentRelationship } from "./relationships";

/**
 * Loads the editorial content JSON into the registry.
 *
 * The content used to register itself: every module under `modules/**` called
 * `registerBlock` at import time, which made a copy edit a code change that the
 * dependency graph traced into every screen importing the package. The JSON is
 * inert, so the edit stops at the data.
 *
 * `namespaces` loads a subset — the directory segment of a data path, e.g.
 * `"testing"`, or `"."` for the root-level article files. That granularity is
 * load-bearing: callers that only want one namespace registered (and tests that
 * assert over exactly what they loaded) relied on importing a single
 * `modules/<ns>` barrel. Called with no argument it loads everything, in the
 * order the side-effect imports used to run, which matters because
 * `registerBlock` is last-write-wins and every `fr` file has to follow its base.
 */

const loaded = new Set<string>();

function namespaceOf(path: string): string {
  const slash = path.indexOf("/");
  return slash === -1 ? "." : path.slice(0, slash);
}

/**
 * Validation runs wherever `__DEV__` is not explicitly false — the app in
 * development, and every node script and test, which is where malformed content
 * can still be introduced. A production bundle ships JSON that was validated at
 * build time and skips re-parsing 400+ blocks on the startup path.
 */
const shouldValidate = typeof __DEV__ === "undefined" || __DEV__;

/**
 * The manifest hands the JSON over as `unknown`, so this is the one place the
 * data becomes a `ContentDataFile` — after the envelope has agreed it is one.
 */
function toContentDataFile(path: string, value: unknown): ContentDataFile {
  if (shouldValidate) {
    const result = contentDataFileSchema.safeParse(value);
    if (!result.success) {
      throw new Error(
        `Invalid content data in src/packages/content/data/${path}: ${JSON.stringify(
          result.error.issues,
          null,
          2,
        )}`,
      );
    }
  }
  return value as ContentDataFile;
}

export function loadContentData(namespaces?: string[]): void {
  const wanted = namespaces ? new Set(namespaces) : undefined;

  for (const entry of contentDataFiles) {
    const path = entry.path;
    if (wanted && !wanted.has(namespaceOf(path))) continue;
    if (loaded.has(path)) continue;

    const data = toContentDataFile(path, entry.data);
    loaded.add(path);

    for (const block of data.blocks) {
      registerBlock(block as ContentBlock);
    }
    for (const relationship of data.relationships) {
      registerRelationship(relationship as ContentRelationship);
    }
    for (const { id, locale, variant } of data.localeVariants) {
      registerLocaleVariant(id as ContentBlock["id"], locale, variant);
    }
  }
}

export const contentNamespaces = Array.from(
  new Set(contentDataFiles.map((file) => namespaceOf(file.path))),
);
